import TickClass from 'lib/TickClass';

/**
 * Monitors keyboard activity to determine whether the user is actively engaged
 */
class KeyboardActivity extends TickClass {
  /**
   * @param  {object} config - configuration object to set specific settings in the instance
   * @param {object} ...params - object of the rest of the parameters to easily pass to
   * super consturctor
   */
  constructor(config, ...params) {
    super(config, ...params);

    this.setupEventListeners = this.setupEventListeners.bind(this);
    this.resetIdle = this.resetIdle.bind(this);
    this.tick = this.tick.bind(this);
    this.releaseEventListeners = this.releaseEventListeners.bind(this);
    this.browserKeyDown = this.browserKeyDown.bind(this);

    this._config = {
      tolerance: 10,
      ...this._config,
      ...config,
    };

    this._keyboardActiveState = true;
    this._keyboardIdleTime = 0;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners that trigger active state changes
   * @private
   */
  setupEventListeners() {
    document.addEventListener('keydown', this.browserKeyDown);
  }

  /**
   * Release event listeners that trigger active state changes
   * @private
   */
  releaseEventListeners() {
    document.removeEventListeners('keydown', this.browserKeyDown);
  }

  /**
   * Increments the idle time every X milliseconds
   * @private
   */
  tick() {
    this._keyboardIdleTime += 1;

    if (this._keyboardActiveState && (this._keyboardIdleTime >= this._config.tolerance)) {
      this._keyboardActiveState = false;
      this.dispatchEvent('statechange', false);
    }
  }

  /**
   * Callback for keyDown listener
   * @private
   */
  browserKeyDown() {
    this._keyboardIdleTime = 0;
    if (!this._keyboardActiveState) {
      this._keyboardActiveState = true;
      this.dispatchEvent('statechange', true);
    }
  }

  /**
   * Resets the idle to 0 or a specified number
   * @param  {Number} buffer - an optional number to set the idle to
   */
  resetIdle(buffer = 0) {
    this._keyboardIdleTime = buffer;
  }

  /**
   * Get the current active state of the activity
   * @return {boolean}
   */
  get active() {
    return this._keyboardActiveState;
  }

  /**
   * Cleanup any event listeners and variable references so it can be cleanly released
   * @param  {...Object} params - any unloading params that might be passed to super's
   * unload function
   */
  unload(...params) {
    this._releaseEventListeners();
    super.unload(...params);
  }
}

export default KeyboardActivity;
